import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, Medal, TrendingUp, Users, MapPin } from 'lucide-react';
import Header from '../components/Header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leaderboard = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    fetchLeaderboard();
  }, [locationFilter]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url = `${API}/sponsors/leaderboard?limit=20`;
      if (locationFilter && locationFilter !== 'all') url += `&province_state=${locationFilter}`;
      
      const response = await axios.get(url);
      setSponsors(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankStyles = (rank) => {
    if (rank === 1) return { bg: 'bg-gradient-to-br from-amber-400 to-amber-500', icon: '🥇', border: 'border-amber-300' };
    if (rank === 2) return { bg: 'bg-gradient-to-br from-neutral-300 to-neutral-400', icon: '🥈', border: 'border-neutral-300' };
    if (rank === 3) return { bg: 'bg-gradient-to-br from-amber-600 to-amber-700', icon: '🥉', border: 'border-amber-500' };
    return { bg: 'bg-neutral-100', icon: rank, border: 'border-neutral-200' };
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-neutral-900 to-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 font-medium px-4 py-2 rounded-full text-sm mb-6">
              <Trophy className="w-4 h-4" />
              Sponsor Leaderboard
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Top Sponsors
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              See which realtors and sponsors are leading the way in connecting with homeowners
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-center gap-4">
            <span className="text-neutral-600 font-medium">Filter by:</span>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-48 rounded-xl" data-testid="leaderboard-location-filter">
                <MapPin className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="Ontario">Ontario</SelectItem>
                <SelectItem value="British Columbia">British Columbia</SelectItem>
                <SelectItem value="Alberta">Alberta</SelectItem>
                <SelectItem value="Quebec">Quebec</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-white rounded-2xl h-20 animate-pulse" />
              ))}
            </div>
          ) : sponsors.length > 0 ? (
            <div className="space-y-4" data-testid="leaderboard-list">
              {sponsors.map((sponsor, index) => {
                const styles = getRankStyles(sponsor.rank);
                return (
                  <motion.div
                    key={sponsor.profile_id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white rounded-2xl p-5 border ${styles.border} hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className={`w-12 h-12 rounded-xl ${styles.bg} flex items-center justify-center text-white font-bold text-xl`}>
                        {sponsor.rank <= 3 ? styles.icon : sponsor.rank}
                      </div>

                      {/* Avatar */}
                      <img
                        src={sponsor.profile_image || 'https://images.pexels.com/photos/7578976/pexels-photo-7578976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                        alt={sponsor.company_name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm"
                      />

                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-neutral-900">{sponsor.company_name || sponsor.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-neutral-500">
                          <span className="capitalize">{sponsor.business_type}</span>
                          {sponsor.service_areas?.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{sponsor.service_areas[0]}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-neutral-900">{sponsor.total_leads || 0}</p>
                          <p className="text-sm text-neutral-500">Leads</p>
                        </div>
                        <div className="text-center hidden sm:block">
                          <p className="text-2xl font-bold text-neutral-900">${(sponsor.total_sponsored || 0).toLocaleString()}</p>
                          <p className="text-sm text-neutral-500">Sponsored</p>
                        </div>
                      </div>

                      {/* Badge */}
                      {sponsor.badge && (
                        <div className="hidden md:flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-sm font-semibold">
                          <Medal className="w-4 h-4" />
                          {sponsor.badge.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 border border-neutral-200 text-center">
              <Trophy className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Sponsors Yet</h3>
              <p className="text-neutral-500">Be the first sponsor in your area!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Leaderboard;
